/**
 * Error Tracking and Monitoring
 * Provides centralized error tracking with optional Sentry integration
 * Falls back gracefully when Sentry is not installed or configured
 */

// Types for error tracking
export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  extra?: Record<string, unknown>;
}

export interface BreadcrumbData {
  category: string;
  message: string;
  level?: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}

// Sentry module interface (dynamic import type)
interface SentryModule {
  init: (options: Record<string, unknown>) => void;
  withScope: (callback: (scope: SentryScope) => void) => void;
  captureException: (error: unknown) => void;
  captureMessage: (message: string, level?: string) => void;
  addBreadcrumb: (breadcrumb: Record<string, unknown>) => void;
  setUser: (user: { id: string; email?: string } | null) => void;
}

interface SentryScope {
  setTag: (key: string, value: string) => void;
  setUser: (user: { id: string; email?: string } | null) => void;
  setExtras: (extras: Record<string, unknown>) => void;
}

// Sentry lazy loading for code splitting
let Sentry: SentryModule | null = null;
let sentryInitialized = false;

/**
 * Initialize Sentry for error tracking
 * Only initializes in production when DSN is configured and Sentry is installed
 */
export async function initErrorTracking(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if no DSN or in development
  if (!dsn || import.meta.env.DEV) {
    if (import.meta.env.DEV) {
      console.info('[ErrorTracking] Development mode - logging errors to console only');
    }
    return;
  }

  try {
    // Dynamically import Sentry for code splitting
    // This will fail gracefully if @sentry/react is not installed
    // Using Function constructor to avoid TypeScript module resolution
    const dynamicImport = new Function('specifier', 'return import(specifier)') as (
      specifier: string
    ) => Promise<SentryModule | null>;

    const sentryModule = await dynamicImport('@sentry/react').catch(() => null);

    if (!sentryModule) {
      console.info('[ErrorTracking] Sentry not installed - errors logged to console only');
      return;
    }

    Sentry = sentryModule;

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: `postpartner@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

      // Performance monitoring
      tracesSampleRate: 0.1, // 10% of transactions for performance

      // Session replay (captures user actions leading to errors)
      replaysSessionSampleRate: 0.0, // Don't record normal sessions
      replaysOnErrorSampleRate: 0.5, // Record 50% of sessions with errors

      // Filter errors
      beforeSend(event: Record<string, unknown>, hint: { originalException?: unknown }) {
        // Don't send errors in development
        if (import.meta.env.DEV) return null;

        // Filter out known non-actionable errors
        const error = hint.originalException;
        if (error instanceof Error) {
          // Network errors during offline - user can retry
          if (error.message.includes('Failed to fetch')) return null;
          if (error.message.includes('NetworkError')) return null;

          // Firebase auth session expiry - user re-authenticates
          if (error.message.includes('auth/user-token-expired')) return null;
        }

        return event;
      },

      // Ignore common third-party errors
      ignoreErrors: [
        // Browser extensions
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        // Third-party scripts
        'ResizeObserver loop',
        'Loading chunk',
        // User actions
        'AbortError',
        'The user aborted a request',
      ],

      // Limit breadcrumb depth
      maxBreadcrumbs: 50,
    });

    sentryInitialized = true;
    console.info('[ErrorTracking] Sentry initialized successfully');
  } catch (error) {
    console.warn('[ErrorTracking] Failed to initialize Sentry:', error);
  }
}

/**
 * Capture an exception with optional context
 */
export function captureException(
  error: Error | unknown,
  context?: ErrorContext
): void {
  // Always log to console in development
  if (import.meta.env.DEV) {
    console.error('[ErrorTracking] Exception:', error, context);
  }

  if (sentryInitialized && Sentry) {
    Sentry.withScope((scope: SentryScope) => {
      if (context?.component) scope.setTag('component', context.component);
      if (context?.action) scope.setTag('action', context.action);
      if (context?.userId) scope.setUser({ id: context.userId });
      if (context?.extra) scope.setExtras(context.extra);

      Sentry!.captureException(error);
    });
  }
}

/**
 * Capture a custom message (for non-exception errors)
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'error',
  context?: ErrorContext
): void {
  if (import.meta.env.DEV) {
    console[level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'info'](
      `[ErrorTracking] ${message}`,
      context
    );
  }

  if (sentryInitialized && Sentry) {
    Sentry.withScope((scope: SentryScope) => {
      if (context?.component) scope.setTag('component', context.component);
      if (context?.action) scope.setTag('action', context.action);
      if (context?.extra) scope.setExtras(context.extra);

      Sentry!.captureMessage(message, level);
    });
  }
}

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(data: BreadcrumbData): void {
  if (sentryInitialized && Sentry) {
    Sentry.addBreadcrumb({
      category: data.category,
      message: data.message,
      level: data.level || 'info',
      data: data.data,
    });
  }
}

/**
 * Set the current user for error context
 */
export function setUser(userId: string | null, email?: string): void {
  if (sentryInitialized && Sentry) {
    if (userId) {
      Sentry.setUser({ id: userId, email });
    } else {
      Sentry.setUser(null);
    }
  }
}
